
const WellIntervention = () => {
  const interventionTechnologies = [
    {
      title: "Coiled Tubing Chemical Delivery",
      description: "Advanced chemical delivery systems using coiled tubing for precise placement and enhanced treatment effectiveness",
      applications: ["Deep well treatments", "Selective zone targeting", "Real-time monitoring"],
      innovations: [
        "GPS-guided chemical placement",
        "Multi-zone treatment capability", 
        "Reduced treatment time by 60%"
      ],
      image: "https://public.readdy.ai/ai/img_res/68dc103a32cb4cf8752e2db6bde02c88.jpg"
    },
    {
      title: "Smart Completion Integration",
      description: "Integration of chemical injection systems with intelligent completion technologies for automated well optimization",
      applications: ["Automated scale inhibition", "Real-time corrosion protection", "Production optimization"],
      innovations: [
        "AI-controlled chemical dosing",
        "Predictive maintenance algorithms",
        "Remote monitoring capabilities"
      ],
      image: "https://readdy.ai/api/search-image?query=Smart%20completion%20integration%20with%20Golden%20Black%20technology%2C%20intelligent%20well%20systems%20with%20automated%20chemical%20injection%20and%20monitoring&width=500&height=300&seq=smart-completion&orientation=landscape"
    },
    {
      title: "Hydraulic Fracturing Enhancement",
      description: "Advanced chemical additives for hydraulic fracturing operations, improving fracture conductivity and proppant placement",
      applications: ["Unconventional reservoirs", "Tight gas formations", "Enhanced permeability"],
      innovations: [
        "Self-healing proppant systems",
        "Conductivity enhancement by 400%",
        "Reduced water requirements"
      ],
      image: "https://readdy.ai/api/search-image?query=Hydraulic%20fracturing%20enhancement%20with%20Golden%20Black%20chemicals%2C%20advanced%20fracturing%20technology%20with%20improved%20proppant%20placement%20systems&width=500&height=300&seq=hydraulic-fracturing&orientation=landscape"
    },
    {
      title: "Wellbore Cleanup Technologies",
      description: "Comprehensive wellbore cleanup solutions using advanced chemical systems for maximum production restoration",
      applications: ["Damage removal", "Debris cleanup", "Production restoration"],
      innovations: [
        "Selective dissolution technology",
        "Minimal formation damage",
        "95% debris removal efficiency"
      ],
      image: "https://readdy.ai/api/search-image?query=Wellbore%20cleanup%20technology%20with%20Golden%20Black%20branding%2C%20advanced%20chemical%20systems%20for%20production%20restoration%20and%20damage%20removal&width=500&height=300&seq=wellbore-cleanup&orientation=landscape"
    }
  ];

  const caseStudies = [
    {
      title: "Offshore Platform Intervention - Fateh Field",
      challenge: "Severe scale buildup in production tubing reducing flow by 70%",
      technique: "Coiled tubing acid treatment with real-time monitoring",
      result: "Full production restoration within 24 hours",
      metrics: ["70% flow rate recovery", "Zero formation damage", "Extended treatment life"]
    },
    {
      title: "Unconventional Gas Well - Khuff Formation",
      challenge: "Low permeability limiting gas production potential",
      technique: "Advanced hydraulic fracturing with conductivity enhancers",
      result: "500% increase in gas production rates",
      metrics: ["5x production increase", "Extended fracture conductivity", "Reduced decline rates"]
    },
    {
      title: "Heavy Oil Recovery - Margham Field",
      challenge: "High viscosity oil requiring enhanced recovery methods",
      technique: "Thermal-chemical stimulation with smart completion",
      result: "300% improvement in oil recovery efficiency",
      metrics: ["3x recovery improvement", "Reduced steam requirements", "Automated optimization"]
    }
  ];

  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-gray-900 mb-6">
            Advanced Well Intervention Techniques
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Golden Black pioneers cutting-edge well intervention technologies that maximize production, 
            extend well life, and optimize reservoir performance through innovative chemical solutions.
          </p>
        </div>

        {/* Intervention Technologies */}
        <div className="space-y-16 mb-20">
          {interventionTechnologies.map((tech, index) => (
            <div key={index} className={`flex flex-col lg:flex-row items-center gap-12 ${index % 2 === 1 ? 'lg:flex-row-reverse' : ''}`}>
              <div className="lg:w-1/2">
                <img 
                  src={tech.image}
                  alt={tech.title}
                  className="w-full h-80 object-cover rounded-xl shadow-lg"
                />
              </div>
              
              <div className="lg:w-1/2">
                <h3 className="text-3xl font-bold text-gray-900 mb-4">{tech.title}</h3>
                <p className="text-lg text-gray-600 mb-6 leading-relaxed">{tech.description}</p>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-3">Applications:</h4>
                    <div className="space-y-2">
                      {tech.applications.map((app, appIndex) => (
                        <div key={appIndex} className="flex items-center">
                          <i className="ri-check-line text-teal-600 mr-2"></i>
                          <span className="text-gray-700 text-sm">{app}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-3">Innovations:</h4>
                    <div className="space-y-2">
                      {tech.innovations.map((innovation, innovationIndex) => (
                        <div key={innovationIndex} className="flex items-center">
                          <i className="ri-star-line text-teal-600 mr-2"></i>
                          <span className="text-gray-700 text-sm">{innovation}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Case Studies */}
        <div className="bg-gray-50 rounded-xl p-8">
          <h3 className="text-3xl font-bold text-gray-900 text-center mb-12">
            Intervention Success Stories
          </h3>
          
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {caseStudies.map((study, index) => (
              <div key={index} className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition-shadow">
                <h4 className="text-xl font-bold text-gray-900 mb-3">{study.title}</h4>
                
                <div className="space-y-4">
                  <div>
                    <h5 className="font-semibold text-red-600 mb-1 flex items-center text-sm">
                      <i className="ri-error-warning-line mr-2"></i>
                      Challenge:
                    </h5>
                    <p className="text-gray-600 text-sm">{study.challenge}</p>
                  </div>
                  
                  <div>
                    <h5 className="font-semibold text-blue-600 mb-1 flex items-center text-sm">
                      <i className="ri-tools-line mr-2"></i>
                      Technique:
                    </h5>
                    <p className="text-gray-600 text-sm">{study.technique}</p>
                  </div>
                  
                  <div>
                    <h5 className="font-semibold text-green-600 mb-1 flex items-center text-sm">
                      <i className="ri-check-line mr-2"></i>
                      Result:
                    </h5>
                    <p className="text-green-700 text-sm font-medium">{study.result}</p>
                  </div>
                  
                  <div>
                    <h5 className="font-semibold text-gray-900 mb-2 text-sm">Key Metrics:</h5>
                    <div className="space-y-1">
                      {study.metrics.map((metric, metricIndex) => (
                        <div key={metricIndex} className="flex items-center">
                          <i className="ri-arrow-right-line text-teal-600 mr-2 text-xs"></i>
                          <span className="text-gray-700 text-xs">{metric}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* CTA Section */}
        <div className="mt-16 text-center">
          <div className="bg-teal-50 rounded-xl p-8">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">
              Transform Your Well Performance
            </h3>
            <p className="text-gray-600 mb-6 max-w-2xl mx-auto">
              Discover how Golden Black's advanced well intervention techniques can unlock 
              your reservoir's full potential and maximize production efficiency.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a 
                href="/consultation" 
                className="bg-teal-600 text-white px-8 py-3 rounded-lg hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer"
              >
                Request Intervention Assessment
              </a>
              <a 
                href="/brochure" 
                className="border border-teal-600 text-teal-600 px-8 py-3 rounded-lg hover:bg-teal-50 transition-colors whitespace-nowrap cursor-pointer"
              >
                Download Technical Brochure
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default WellIntervention;
